/**
 * A node managed from the console (workbench D290): what it is doing now, followed live over the relay
 * (`node.snapshot`, then `node.subscribe` as a long poll), and what may be changed, offered only as far as
 * the node's policy allows (`snapshot.allowed`). Interactive things (an approval, stopping a member,
 * restarting the service, checking or installing an update) are relay calls with `via: "web"`; changes to
 * how the node is set up go as device orders and are answered on its next report.
 */
import {
  NODE_CONTROL_ACTION_WORDS,
  type NodeApprovalView,
  type NodeControlAction,
  type NodeHostView,
  type NodeLiveEvent,
  type NodeRead,
  type NodeSnapshot,
} from '@aioe/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { problemText, routes } from '../api/client.ts';
import { timeAgo } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';

const LOG_KEEP = 200;
const BACKOFF = [5_000, 15_000, 60_000];

type Live = { snap?: NodeSnapshot; reachable: boolean; reconnecting: boolean; error?: string };

/** One JSON-RPC call over the relay; a JSON-RPC error comes back as its sentence. */
async function call<T>(deviceId: string, method: string, params: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const r = await routes.rpc(deviceId, method, params, undefined, signal);
  if (r.error) throw new Error(r.error.message);
  return r.result as T;
}

function apply(snap: NodeSnapshot, e: NodeLiveEvent): NodeSnapshot {
  switch (e.kind) {
    case 'status':
      return { ...snap, running: e.running, words: e.words };
    case 'hosts':
      return { ...snap, hosts: e.hosts };
    case 'approval':
      return { ...snap, approvals: [...snap.approvals.filter((a) => a.permissionId !== e.permissionId), e as unknown as NodeApprovalView] };
    case 'approval.answered':
      return { ...snap, approvals: snap.approvals.filter((a) => a.permissionId !== e.permissionId) };
    case 'log':
      return { ...snap, log: [...snap.log, ...e.lines].slice(-LOG_KEEP) };
    case 'monitor':
      return { ...snap, monitor: { sample: e.sample, ...(e.node ? { node: e.node } : {}) } };
    case 'managed':
      return { ...snap, ...(e.managed ? { managed: e.managed } : { managed: undefined }) };
    case 'order':
      return { ...snap, orders: [{ sentence: e.sentence, state: e.state, at: e.at }, ...snap.orders].slice(0, 20) };
    default:
      return snap;
  }
}

/** The node as it is now: a snapshot, then events; offline answers keep what was drawn and retry with backoff. */
function useNodeLive(deviceId: string, online: boolean): Live & { refresh: () => void } {
  const [live, setLive] = useState<Live>({ reachable: online, reconnecting: false });
  const [epoch, setEpoch] = useState(0);
  const refresh = useCallback(() => setEpoch((n) => n + 1), []);
  useEffect(() => {
    const ctl = new AbortController();
    let after: string | undefined;
    let failures = 0;
    const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
    void (async () => {
      while (!ctl.signal.aborted) {
        try {
          if (!after) {
            const snap = await call<NodeSnapshot>(deviceId, 'node.snapshot', { logLines: 50 }, ctl.signal);
            const first = await call<NodeRead>(deviceId, 'node.subscribe', { waitMs: 0, limit: 1 }, ctl.signal);
            after = first.after;
            setLive({ snap, reachable: true, reconnecting: false });
            failures = 0;
            continue;
          }
          const read = await call<NodeRead>(deviceId, 'node.subscribe', { after, waitMs: 20_000, limit: 200, logLines: 50 }, ctl.signal);
          failures = 0;
          if (read.resync && read.snapshot) {
            setLive({ snap: read.snapshot as NodeSnapshot, reachable: true, reconnecting: false });
          } else if (read.events.length) {
            setLive((l) => (l.snap ? { ...l, snap: read.events.reduce(apply, l.snap), reachable: true, reconnecting: false } : l));
          }
          after = read.after;
          if (read.retryAfterMs) await wait(read.retryAfterMs);
        } catch (e) {
          if (ctl.signal.aborted) return;
          const text = problemText(e);
          const offline = /offline|not reachable|409/i.test(text);
          setLive((l) => ({ ...l, reachable: !offline && l.reachable, reconnecting: true, error: offline ? undefined : text }));
          await wait(BACKOFF[Math.min(failures++, BACKOFF.length - 1)]!);
          after = undefined;
        }
      }
    })();
    return () => ctl.abort();
  }, [deviceId, epoch]);
  return { ...live, refresh };
}

function HostRow({ h, may, onStop }: { h: NodeHostView; may: boolean; onStop: (rootId: string, sessionId: string) => void }) {
  return (
    <li className="grid gap-1 py-2">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate font-medium">{h.repository}</span>
        <StatusMark state={h.state === 'running' || h.state === 'ready' ? 'online' : h.state === 'error' ? 'error' : 'idle'} label={h.state === 'running' ? `${h.agents} working` : h.state} />
      </div>
      {h.message ? <div className="text-xs text-muted">{h.message}</div> : null}
      {h.members.map((m) => (
        <div key={m.sessionId} className="flex min-w-0 items-center justify-between gap-2 text-xs text-muted">
          <span className="min-w-0">
            {m.agentName}
            {m.for ? ` for ${m.for}` : ''}
            {m.task ? ` · ${m.task}` : ''}
          </span>
          {may && (
            <Button size="sm" variant="ghost" onPress={() => onStop(h.rootId, m.sessionId)}>
              Stop
            </Button>
          )}
        </div>
      ))}
    </li>
  );
}

function ApprovalCard({ a, may, onAnswer }: { a: NodeApprovalView; may: boolean; onAnswer: (a: NodeApprovalView, decision: 'allow' | 'deny') => void }) {
  return (
    <li className="grid gap-1 border-t border-separator py-3 text-sm first:border-t-0 first:pt-1">
      <div className="font-medium">{a.title ?? `${a.agentName ?? 'An agent'} asks to go on`}</div>
      <div className="text-xs text-muted">
        {a.repository} · {a.risk}
        {a.askedAt ? ` · ${timeAgo(a.askedAt)}` : ''}
      </div>
      {a.ownerRequired && <div className="text-xs text-muted">An owner must approve this.</div>}
      {may && (
        <div className="flex gap-2">
          <Button size="sm" onPress={() => onAnswer(a, 'allow')}>
            Approve
          </Button>
          <Button size="sm" variant="ghost" onPress={() => onAnswer(a, 'deny')}>
            Refuse
          </Button>
        </div>
      )}
    </li>
  );
}

/** The node page's live view and management, offered only as far as the node's policy allows. */
export function NodeManage({ device }: { device: { deviceId: string; hostname: string; online: boolean; lastSeenAt: string; name?: string } }) {
  const live = useNodeLive(device.deviceId, device.online);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const [routine, setRoutine] = useState('');
  const [settingKey, setSettingKey] = useState('');
  const [settingValue, setSettingValue] = useState('');
  const [channel, setChannel] = useState<'stable' | 'nightly'>();
  const busy = useRef(false);
  const snap = live.snap;
  const allowed = new Set<NodeControlAction>(snap?.allowed ?? []);
  const may = (a: NodeControlAction) => live.reachable && allowed.has(a);
  const name = snap?.name ?? device.name ?? device.hostname;

  const act = async (what: () => Promise<unknown>, said: string) => {
    if (busy.current) return;
    busy.current = true;
    setError(undefined);
    try {
      await what();
      setDone(said);
      live.refresh();
    } catch (e) {
      setError(problemText(e));
    } finally {
      busy.current = false;
    }
  };
  const order = (kind: Parameters<typeof routes.orderDevice>[1], body: Record<string, unknown>, said: string) => act(() => routes.orderDevice(device.deviceId, kind, body), said);
  const rpc = (method: string, params: Record<string, unknown>, said: string) => act(() => call(device.deviceId, method, { ...params, via: 'web' }), said);

  if (!live.reachable && !snap) {
    return (
      <ListGroup title="Live" description="What this node is doing now." actions={<StatusMark state="offline" label="Not reachable" />}>
        <ListBlock className="text-sm text-muted">
          {name} is not reachable. It was last seen {timeAgo(device.lastSeenAt)}. Changes ordered below still wait for its next report.
        </ListBlock>
      </ListGroup>
    );
  }

  return (
    <>
      <ListGroup
        title="Live"
        description={live.reachable ? (snap?.managed ? snap.managed.words : 'What this node is doing now.') : `Not reachable: last seen ${timeAgo(device.lastSeenAt)}; showing what was last drawn.`}
      >
        <ListBlock>
          <StatusMark wrap state={!live.reachable ? 'offline' : live.reconnecting ? 'working' : snap?.running ? 'online' : 'idle'} label={!live.reachable ? 'Not reachable' : live.reconnecting ? 'Reconnecting' : (snap?.words ?? 'Connecting')} />
        </ListBlock>
        {error ?? live.error ? (
          <ListBlock>
            <ErrorNote error={error ?? live.error} />
          </ListBlock>
        ) : null}
        {done && !error ? <ListBlock className="text-xs text-muted">{done}</ListBlock> : null}
        {snap?.approvals.length ? (
          <ListBlock className="grid gap-2">
            <h3 className="m-0 text-xs font-medium text-muted">Waiting for a person</h3>
            <ul className="m-0 grid list-none p-0">
              {snap.approvals.map((a) => (
                <ApprovalCard
                  key={a.permissionId}
                  a={a}
                  may={may('approvals')}
                  onAnswer={(x, decision) => void rpc('node.permissions.resolve', { rootId: x.rootId, sessionId: x.sessionId, permissionId: x.permissionId, decision }, decision === 'allow' ? 'Approved.' : 'Refused.')}
                />
              ))}
            </ul>
          </ListBlock>
        ) : null}
        {snap?.hosts.length ? (
          <ListBlock className="grid gap-1 text-sm">
            <h3 className="m-0 text-xs font-medium text-muted">Repositories</h3>
            <ul className="m-0 grid list-none divide-y divide-separator p-0">
              {snap.hosts.map((h) => (
                <HostRow key={h.rootId} h={h} may={may('members')} onStop={(rootId, sessionId) => void rpc('node.sessions.stop', { rootId, sessionId }, 'Asked the member to stop.')} />
              ))}
            </ul>
          </ListBlock>
        ) : null}
        {snap?.monitor ? (
          <ListBlock className="text-xs text-muted tabular-nums">
            CPU {Math.round(snap.monitor.sample.cpu.percent)}% · memory {Math.round((snap.monitor.sample.ram.used / Math.max(1, snap.monitor.sample.ram.total)) * 100)}%
            {snap.monitor.sample.serving ? ` · serving ${snap.monitor.sample.serving.model}` : ''}
          </ListBlock>
        ) : null}
        {snap?.log.length ? (
          <ListBlock>
            <details>
              <summary className="cursor-pointer text-xs text-muted">The log, newest last</summary>
              <pre className="mono m-0 mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-surface-secondary p-3 text-xs">{snap.log.slice(-50).join('\n')}</pre>
            </details>
          </ListBlock>
        ) : null}
        {snap?.history.length ? (
          <ListBlock>
            <details>
              <summary className="cursor-pointer text-xs text-muted">What was done from here</summary>
              <ul className="m-0 mt-2 grid list-none gap-1 p-0 text-xs text-muted">
                {snap.history.slice(0, 20).map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    {h.sentence} · {timeAgo(h.at)}
                  </li>
                ))}
              </ul>
            </details>
          </ListBlock>
        ) : null}
        {!snap && live.reachable ? <ListBlock className="text-sm text-muted">Connecting to {name}…</ListBlock> : null}
      </ListGroup>

      <ListGroup
        title="Controls"
        description={snap && snap.allowed.length > 0 ? `The node’s policy allows: ${snap.allowed.map((a) => NODE_CONTROL_ACTION_WORDS[a]).join(', ')}.` : snap ? 'This node’s policy allows watching only.' : 'What may be changed from here, as far as the node’s policy allows.'}
      >
        {(may('service') || !snap) && (
          <ListRow
            title="Start when the machine starts"
            control={<ChoiceSelect label="Start at login" value={undefined} options={{ on: 'Start at login', off: 'Do not start at login' }} placeholder="Start at login…" isDisabled={!may('service')} onChange={(v) => void order('service.configure', { startAtLogin: v === 'on' }, v === 'on' ? 'Asked it to start at login.' : 'Asked it not to start at login.')} />}
          />
        )}
        {(may('service') || !snap) && (
          <ListRow
            title="Restart"
            description="It comes back in a few seconds."
            control={
              <Button size="sm" variant="secondary" isDisabled={!may('service')} onPress={() => void rpc('node.service.restart', {}, 'Restarting: it comes back in a few seconds.')}>
                Restart
              </Button>
            }
            compactControl
          />
        )}
        {(may('updates') || !snap) && (
          <ListRow
            title="Update channel"
            description="Which builds it follows."
            control={
              <>
                <ChoiceSelect label="Update channel" value={channel} options={{ stable: 'Stable', nightly: 'Nightly' }} onChange={setChannel} isDisabled={!may('updates')} placeholder="Channel" />
                <Button size="sm" variant="secondary" isDisabled={!may('updates') || !channel} onPress={() => void order('update.configure', { channel }, `Asked it to follow the ${channel} channel.`)}>
                  Use channel
                </Button>
              </>
            }
          />
        )}
        {(may('updates') || !snap) && (
          <ListRow
            title="Install the newest update"
            control={
              <Button size="sm" variant="secondary" isDisabled={!may('updates')} onPress={() => void rpc('node.updates.install', {}, 'Installing the newest update.')}>
                Install update
              </Button>
            }
            compactControl
          />
        )}
        {may('routines') && (
          <ListRow
            title="A routine"
            description="Run one now, or remove it from the node, by its id."
            control={
              <>
                <Button size="sm" variant="secondary" isDisabled={!routine.trim()} onPress={() => void order('routine.run', { id: routine.trim() }, `Asked it to run ${routine.trim()}.`)}>
                  Run
                </Button>
                <Button size="sm" variant="ghost" isDisabled={!routine.trim()} onPress={() => void order('routine.remove', { id: routine.trim() }, `Asked it to remove ${routine.trim()}.`)}>
                  Remove
                </Button>
              </>
            }
          >
            <input className={`${inputClass} w-full`} aria-label="Routine" placeholder="Routine id" value={routine} onChange={(e) => setRoutine(e.target.value)} />
          </ListRow>
        )}
        {may('remote-sessions') && (
          <ListRow
            title="Remote control of every session of the node’s person"
            control={<ChoiceSelect label="Remote control of sessions" value={undefined} options={{ all: 'Every session', one: 'One by one' }} placeholder="Choose…" onChange={(v) => void order('remote.configure', { allSessions: v === 'all' }, v === 'all' ? 'Asked it to open every session to remote control.' : 'Asked it to open sessions one by one only.')} />}
          />
        )}
        {may('settings') && (
          <ListRow
            title="A node setting"
            description="Its key starts with node. An empty value clears it."
            control={
              <Button size="sm" variant="secondary" isDisabled={!settingKey.startsWith('node.')} onPress={() => void order('node.setting.set', { key: settingKey, value: settingValue === '' ? null : settingValue }, `Asked it to set ${settingKey}.`)}>
                Set
              </Button>
            }
          >
            <div className="flex flex-wrap gap-2">
              <input className={`${inputClass} mono min-w-0 flex-1`} aria-label="Setting" placeholder="node.setting" value={settingKey} onChange={(e) => setSettingKey(e.target.value)} />
              <input className={`${inputClass} min-w-0 flex-1`} aria-label="Value" placeholder="Value" value={settingValue} onChange={(e) => setSettingValue(e.target.value)} />
            </div>
          </ListRow>
        )}
      </ListGroup>
    </>
  );
}
